import {createAction, createSlice} from '@reduxjs/toolkit';
import {combineEpics, ofType} from 'redux-observable';
import {
    catchError,
    mergeMap,
    repeatWhen,
    exhaustMap,
    takeUntil,
    delay,
    filter,
} from 'rxjs/operators';
import {from, of, EMPTY} from 'rxjs';
import type {ActionCreatorWithPayload} from '@reduxjs/toolkit';

import {
    WikiForcedSyncResponse,
    WikiForcedSyncArgs,
    WikiForcedSyncComArgs,
} from '~shared/schema/wiki/actions/forced-sync';

import {WikiEpicDeps} from '~ui/store/deps';

import {WikiEpic} from '../types';

const POLLING_DELAY = 3000;
const MAX_POLLING_ERRORS = 2;

interface SetStatusPayload {
    orgId?: string;
    status: string;
    error?: string;
}

type StartPollingPayload = WikiForcedSyncArgs;

type StartComPollingPayload = WikiForcedSyncComArgs;

export interface HiThereState {
    orgId?: string;
    status: string;
    error?: string;
}

// Ставлю сразу in_progress, так как у нас только один процесс пока
// Когда добавим выбор контекста поставить initial
const initialState: HiThereState = {
    status: 'in_progress',
};

const setHiThereStatus = createAction<SetStatusPayload>('hi-there/setStatus');
const startHiTherePolling = createAction<StartPollingPayload>('hi-there/startPolling');
const startComHiTherePolling = createAction<StartComPollingPayload>('hi-there/startComPolling');
const stopHiTherePolling = createAction('hi-there/stopPolling');

const {reducer} = createSlice({
    name: 'hi-there',
    initialState,
    reducers: {},
    extraReducers: (builder) =>
        builder.addCase(setHiThereStatus, (state, {payload}) => {
            state.error = payload.error;
            state.status = payload.status;
            state.orgId = payload.orgId;
        }),
});

const onHiThereStatusSetEpic: WikiEpic = (action$) =>
    action$.pipe(
        filter(setHiThereStatus.match),
        mergeMap(({payload: {status}}) => {
            if (status === 'ok' || status === 'failed') {
                return of(stopHiTherePolling());
            }

            return EMPTY;
        }),
    );

const makePollingEpic =
    <Args>(
        request: (data: Args, deps: WikiEpicDeps) => Promise<WikiForcedSyncResponse>,
        listenAction: ActionCreatorWithPayload<Args>,
    ): WikiEpic =>
    (action$, _state$, deps) => {
        return action$.pipe(
            filter(listenAction.match),
            exhaustMap((action) => {
                let errorsCount = 0;

                return of(action).pipe(
                    // Нужно поллить с одинаковыми параметрами
                    mergeMap(({payload}) => {
                        return from(request(payload, deps)).pipe(
                            mergeMap((res) => {
                                const {status, org_id: orgId, error_code: error} = res;

                                errorsCount = 0;

                                return of(setHiThereStatus({status, orgId, error}));
                            }),
                        );
                    }),
                    catchError(() => {
                        errorsCount++;

                        if (errorsCount >= MAX_POLLING_ERRORS) {
                            return of(setHiThereStatus({status: 'failed', error: 'unknown'}));
                        }

                        return EMPTY;
                    }),
                    repeatWhen((complete$) => complete$.pipe(delay(POLLING_DELAY))),
                );
            }),
            // stop on action
            takeUntil(action$.pipe(ofType(stopHiTherePolling.type))),
        );
    };

export const startHiTherePollingEpic = makePollingEpic(
    (data: WikiForcedSyncArgs, {sdk}) =>
        sdk.wiki.forcedSync({
            orgId: data.orgId,
            cloudOrgId: data.cloudOrgId,
        }),
    startHiTherePolling,
);

export const startComHiTherePollingEpic = makePollingEpic(
    (data: WikiForcedSyncComArgs, {sdk}) =>
        sdk.wiki.forcedSyncCom({
            collabId: data.collabId,
            identity: data.identity,
        }),
    startComHiTherePolling,
);

const epic = combineEpics<WikiEpic>(
    startHiTherePollingEpic,
    onHiThereStatusSetEpic,
    startComHiTherePollingEpic,
);

export {epic, reducer, startHiTherePolling, stopHiTherePolling, startComHiTherePolling};
